import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { getReportExtraFundsView } from "../../utils/reportExtraFunds";
import {
  Container,
  Spinner,
  Alert,
  Table,
  Row,
  Col,
  Card,
  Image
} from "react-bootstrap";

const UserReportDetails = () => {
  const { id } = useParams();
  const { user: currentUser } = useAuth();
  const { t } = useTranslation();
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [includedDebts, setIncludedDebts] = useState([]);
  const [activeDebts, setActiveDebts] = useState([]);

  useEffect(() => {
    if (!currentUser?.token) {
      setError(t("notAuthorized"));
      setLoading(false);
      return;
    }

    axios
      .get(`/api/user/report/${id}`, {
        headers: {
          Authorization: `Bearer ${currentUser.token}`,
        },
      })
      .then((res) => {
        setReport(res.data);
        const debtIds = res.data?.report_json?.selectedDebts;
        if (Array.isArray(debtIds) && debtIds.length > 0) {
          axios
            .get(`/api/debts/byIds?ids=${debtIds.join(",")}`, {
              headers: {
                Authorization: `Bearer ${currentUser.token}`,
              },
            })
            .then((debtRes) => setIncludedDebts(debtRes.data))
            .catch((err) => console.error("Помилка при завантаженні боргів:", err));
        }
      })
      .catch((err) => {
        console.error("❌ Помилка при завантаженні звіту:", err);
        console.error("❌ Відповідь сервера:", err.response?.data);
        setError(t("fetchReportError"));
      })
      .finally(() => setLoading(false));
  }, [id, currentUser, t]);

  useEffect(() => {
    if (!report?.driver_id || !currentUser?.token) return;

    axios
      .get(`/api/debts/active?driver_id=${report.driver_id}`, {
        headers: {
          Authorization: `Bearer ${currentUser.token}`,
        },
      })
      .then((res) => {
        setActiveDebts(res.data);
      })
      .catch((err) => {
        console.error("❌ Помилка при отриманні боргів з БД:", err);
      });
  }, [report, currentUser]);

  if (loading) return <Spinner animation="border" variant="primary" className="m-4" />;
  if (error) return <Alert variant="danger">{error}</Alert>;
  if (!report) return null;

  // Парсимо JSON якщо він прийшов як рядок
  const reportJson = (() => {
    try {
      return typeof report.report_json === 'string' 
        ? JSON.parse(report.report_json) 
        : (report.report_json || {});
    } catch (e) {
      console.error("❌ Помилка парсингу report_json:", e);
      return {};
    }
  })();
  
  const taxData = reportJson.taxData || {};
  const services = ["uber", "bolt", "freenow"];

  const serviceData = {
    uber: {
      brutto: parseFloat(reportJson?.report?.uber?.[0]?.incBrutto || 0),
      payout: parseFloat(reportJson?.report?.uber?.[0]?.incNetto || 0),
      vat: parseFloat(taxData?.uber?.tax || 0),
      total: parseFloat(taxData?.uber?.total || 0),
      cashReceived: parseFloat(reportJson?.report?.uber?.[0]?.incCash * -1 || 0),

    },
    bolt: {
      brutto: parseFloat(reportJson?.report?.bolt?.[0]?.incBrutto || 0),
      payout: parseFloat(reportJson?.report?.bolt?.[0]?.totalInc || 0),
      vat: parseFloat(taxData?.bolt?.tax || 0),
      total: parseFloat(taxData?.bolt?.total || 0),
      cashReceived: parseFloat(reportJson?.report?.bolt?.[0]?.incNettoCash || 0),

    },
    freenow: {
      brutto: parseFloat(reportJson?.report?.freenow?.[0]?.brutto || 0),
      payout: parseFloat(reportJson?.report?.freenow?.[0]?.saldo_freenow || 0),
      vat: parseFloat(taxData?.freenow?.tax || 0),
      total: parseFloat(taxData?.freenow?.total || 0),
      cashReceived: parseFloat(reportJson?.report?.freenow?.[0]?.cash || 0), // якщо є поле

    },
  };


  const extraFunds = reportJson.extraFunds || {};
  const { rows: filteredExtraFunds, refundsItems, otherExpensesItems } = getReportExtraFundsView(extraFunds);

  const getServiceLogo = (service) => `/images/services/${service}_logo.png`;

  // Функція для конвертації номера тижня в дати
  const getWeekDates = (weekNumber) => {
    try {
      // Номер тижня має формат YYYYWW (наприклад, 202539)
      const year = Math.floor(weekNumber / 100);
      const week = weekNumber % 100;
      
      // Створюємо дату для першого дня року
      const jan1 = new Date(year, 0, 1);
      
      // Знаходимо понеділок першого тижня
      const dayOfWeek = jan1.getDay();
      const firstMonday = new Date(jan1);
      firstMonday.setDate(jan1.getDate() + (dayOfWeek === 0 ? 1 : 8 - dayOfWeek));
      
      // Додаємо тижні до потрібного тижня
      const targetWeekStart = new Date(firstMonday);
      targetWeekStart.setDate(firstMonday.getDate() + (week - 1) * 7);
      
      // Неділя тижня
      const targetWeekEnd = new Date(targetWeekStart);
      targetWeekEnd.setDate(targetWeekStart.getDate() + 6);
      
      return {
        start: targetWeekStart,
        end: targetWeekEnd,
        year: year
      };
    } catch (error) {
      console.error("Помилка парсингу тижня:", error);
      return null;
    }
  };

  const weekDates = getWeekDates(parseInt(report.week_number));

  return (
    <Container className="mt-4">
      <h2>
        {t("reportForWeek")} {weekDates ? 
          `${weekDates.start.toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit' })} - ${weekDates.end.toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit' })} ${weekDates.year}` 
          : report.week_number
        }
      </h2>
      <p><strong>{t("createdAt")}</strong>: {new Date(report.created_at).toLocaleDateString()}</p>

      <Row className="mt-4">
        {services.map((service) => (
          <Col md={4} key={service}>
            <Card className="mb-3">
              <Card.Body>
                <div className="text-center mb-3">
                  <Image src={getServiceLogo(service)} height={60} alt={service} />
                </div>
                <p><strong>{t("brutto")}</strong>: {serviceData[service].brutto.toFixed(2)} zł</p>
                <p><strong>{t("cashReceived")}</strong>: {serviceData[service].cashReceived.toFixed(2)} zł</p>

                <p><strong>{t("payout")} {service}:</strong> {serviceData[service].payout.toFixed(2)} zł</p>
                <p><strong>VAT 4%</strong>: {serviceData[service].vat.toFixed(2)} zł</p>
                <p><strong>{t("amount")}</strong>: {serviceData[service].total.toFixed(2)} zł</p>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Alert variant="info" className="mb-3">
        <strong>ℹ {t("explanationTitle")}:</strong>
        <ul className="mb-0 mt-2 ps-3">
          <li><strong>{t("brutto")}:</strong> {t("explanationBrutto")}</li>
          <li><strong>{t("payout")}:</strong> {t("explanationPayout")}</li>
          <li><strong>VAT:</strong> {t("explanationVAT")}</li>
          <li><strong>{t("amount")}:</strong> {t("explanationTotal")}</li>
        </ul>
      </Alert>

      {filteredExtraFunds.length > 0 && (
        <Card className="mb-3">
          <Card.Body>
            <h5>{t("additionalFunds")}</h5>
            <Table bordered>
              <tbody>
                {filteredExtraFunds.map(([key, val]) => (
                  <tr key={key}>
                    <td>
                      {key === "refunds" ? (
                        <div>
                          <div className="fw-bold">{t(`extraFunds.${key}`, { defaultValue: key })}</div>
                          {refundsItems.length > 0 && (
                            <div className="mt-1">
                              {refundsItems.map((item, idx) => (
                                <div key={idx} className="small text-muted">
                                  {item.description}: {parseFloat(item.amount).toFixed(2)} zł
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : key === "other" ? (
                        <div>
                          <div className="fw-bold">{t(`extraFunds.${key}`, { defaultValue: key })}</div>
                          {otherExpensesItems.length > 0 && (
                            <div className="mt-1">
                              {otherExpensesItems.map((item, idx) => (
                                <div key={idx} className="small text-muted">
                                  {item.description}: {parseFloat(item.amount).toFixed(2)} zł
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        t(`extraFunds.${key}`, { defaultValue: key })
                      )}
                    </td>
                    <td>
                      {Array.isArray(val)
                        ? val.map((item, idx) => (
                          <div key={idx}>{item.description}: {item.amount} zł</div>
                        ))
                        : (
                          <div className="fw-bold">
                            {(() => {
                              // If value is missing or 0 but we have items, calculate from items
                              if (key === "refunds" && refundsItems.length > 0) {
                                const calculatedTotal = refundsItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
                                return (parseFloat(val) || calculatedTotal).toFixed(2);
                              }
                              if (key === "other" && otherExpensesItems.length > 0) {
                                const calculatedTotal = otherExpensesItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
                                return (parseFloat(val) || calculatedTotal).toFixed(2);
                              }
                              return (parseFloat(val) || 0).toFixed(2);
                            })()} zł
                          </div>
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {includedDebts.length > 0 && (
        <Card className="mb-3">
          <Card.Body>
            <h5>{t("includedDebts")}</h5>
            <Table bordered>
              <thead>
                <tr>
                  <th>{t("title")}</th>
                  <th>{t("description")}</th>
                  <th>{t("amount")}</th>
                  <th>{t("date")}</th>
                </tr>
              </thead>
              <tbody>
                {includedDebts.map((debt) => (
                  <tr key={debt.id}>
                    <td>{debt.description}</td>
                    <td>{debt.details || "-"}</td>
                    <td>{debt.amount} zł</td>
                    <td>{new Date(debt.date).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {activeDebts.length > 0 && (
        <Card className="mb-3 border-danger" style={{ backgroundColor: "#fff5f5" }}>
          <Card.Body>
            <h5 className="text-danger fw-bold">{t("activeDebtsTitle")}</h5>
            <ul className="list-group">
              {activeDebts.map((debt) => (
                <li key={debt.id} className="list-group-item d-flex justify-content-between align-items-start">
                  <div className="ms-2 me-auto">
                    <div className="fw-bold text-dark">{debt.description}</div>
                    <details className="text-muted" style={{ fontSize: "0.9rem" }}>
                      <summary>{t("showDetails")}</summary>
                      {debt.details || t("noDetails")}
                    </details>
                    <small className="text-muted">{new Date(debt.date).toLocaleDateString()}</small>
                  </div>
                  <span className="badge bg-danger rounded-pill">{parseFloat(debt.amount).toFixed(2)} zł</span>
                </li>
              ))}
            </ul>
          </Card.Body>
        </Card>
      )}

      <Card className="mb-3 bg-success-subtle">
        <Card.Body>
          <h5 className="text-success">{t("payoutToAccount")}:</h5>
          <h3>{reportJson.payout} zł</h3>
          {extraFunds.sallary && parseFloat(extraFunds.sallary) !== 0 && (
            <div className="mt-2">
              <p>
                <strong>{t("extraFunds.sallary", { defaultValue: "Sallary" })}:</strong> {parseFloat(extraFunds.sallary).toFixed(2)} zł
              </p>
            </div>
          )}
        </Card.Body>
      </Card>


      <Alert variant="secondary" className="text-center mb-3">
        {t("calculationDisclaimer")}
      </Alert>
    </Container>
  );
};

export default UserReportDetails;
